/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from 'react';
import {
  Step, StepLabel, Stepper, Box, Typography, Fade, StepButton,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import Grid from '@mui/material/Grid';
import FormWizard from '../FormWizard';
import { STEPS, startValues } from './initialValues';
import MobileStepIcon from '../MobileStepIcon';
import FirstStep from './Steps/First';
import SecondStep from './Steps/Second';
import ThirdStep from './Steps/Third';
import FourthStep from './Steps/Fourth';
import Confirm from './Steps/Confirm';
import { NavButton } from '../FormWizard/Navigation/NavigationButtons';
import FirstStepSchemaValidator from './Validations/FirstStepSchemaValidator';
import SecondStepSchemaValidator from './Validations/SecondStepSchemaValidator';
import ThirdStepSchemaValidator from './Validations/ThirdStepSchemaValidator';
import FourthStepSchemaValidator from './Validations/FourthStepSchemaValidator';
import FifthStepSchemaValidator from './Validations/FifthStepSchemaValidator';
import './styles.scss';
import { useAppContext } from '../../context/appContext';
import ACTIONS from '../../context/appActions';
import GetEstimateBenefits from '../../api/services/EstimatedBenefitsService';

const Wizard = () => {
  const { dispatch } = useAppContext();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  const {
    store: {
      benefitEstimator,
    },
  } = useAppContext();

  const handleOnSubmit = (values) => {
    setIsSubmitLoading(true);
    const isSingle = values?.benefitEstimator?.filingStatus === 'single';
    const dataPayload = {
      primaryCurrentAge: values?.benefitEstimator?.currentAge,
      secondaryCurrentAge: isSingle ? null : values?.benefitEstimator?.spouseCurrentAge,
      primarySocialSecurityStartAge: values?.benefitEstimator?.socialSecurityStartAge,
      secondarySocialSecurityStartAge: isSingle ? null : values?.benefitEstimator?.spouseSocialSecurityStartAge,
      primaryMonthlySocialSecurityIncome: values?.benefitEstimator?.initialSocialSecurityBenefit,
      secondaryMonthlySocialSecurityIncome: isSingle ? null : values?.benefitEstimator?.spouseInitialSocialSecurityBenefit,
      decreaseinSocialSecurityBenefits: ((values?.benefitEstimator?.potentialReductionEstimated || 0) / 100),
      illustratedRate: 1,
      productVariant: process.env.REACT_APP_NAME || 'Choice',
    };

    GetEstimateBenefits(dataPayload).then((data) => {
      const payload = {
        benefitEstimator: values,
        estimatedBenefits: {
          potentialReduction: values?.benefitEstimator?.potentialReductionEstimated,
          monthlyReduction: data.initialMonthlyBenefitCut,
          lifetimeReduction: data.lifeBenefitCult,
        },
        beforeReductionColumn: [0, data.lifeBenefitCult],
        afterReductionFirstColumn: [data.promisedLifetimeBenefitsPostReduction, 0],
        afterReductionSecondColumn: [0, (data.promisedLifetimeBenefitsPostReduction - data.lifeBenefitCult)],
        submitted: true,
      };
      dispatch({ type: ACTIONS.UPDATE_STATE, data: { ...payload, page: 'results' } });
      window.scrollTo(0, 0);
      setIsSubmitLoading(false);
    }).catch(() => {
      dispatch({ type: ACTIONS.UPDATE_STATE, data: { hasError: true } });
      setIsSubmitLoading(false);
    });
  };

  const handleSteps = (step) => {
    switch (step) {
      case 0:
        return <FirstStep />;
      case 1:
        return <SecondStep />;
      case 2:
        return <ThirdStep />;
      case 3:
        return <FourthStep />;
      case 4:
        return <Confirm />;
      default:
        throw new Error('Unknown step');
    }
  };
  const largeScreen = useMediaQuery((theme) => theme.breakpoints.up('md'));
  const mediumScreen = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  return (
    <FormWizard
      submitOnStep={STEPS.POTENTIAL_REDUCTION_ESTIMATED}
      initialValues={benefitEstimator || startValues}
      handleSubmit={handleOnSubmit}
      validationsSchema={[
        FirstStepSchemaValidator,
        SecondStepSchemaValidator,
        ThirdStepSchemaValidator,
        FourthStepSchemaValidator,
        FifthStepSchemaValidator,
      ]}
    >
      {({ currentStep, defaultSteps, values }) => (
        <>
          <Fade in={currentStep === STEPS.FILING_STATUS} timeout={1000}>
            <Box mb={{ xs: 3 }} ml={{ xs: 0, md: 11, lg: 18.75 }} mr={{ xs: 2, md: 6.25 }}>
              <Typography variant='subtitle1' className='lifetime-estimator-description-mobile'>
                Complete the following questions to calculate your lifetime Social
                Security benefits and explore what a potential reduction could mean
                to you.
              </Typography>
            </Box>
          </Fade>
          <div className={`wizard-wrapper ${currentStep > 0 ? 'next-steps' : ''}`}>
            <FormWizard.Stepper>
              {(handleOnClick) => (
                <Stepper activeStep={currentStep} alternativeLabel className='lifetime-estimator-wizard-stepper'>
                  {[...defaultSteps.map(({ stepName, isCompleted: completedStep }, stepIndex) => (
                    <Step key={stepName} completed={false} onClick={() => completedStep && currentStep !== stepIndex && handleOnClick(stepIndex)}>
                      <StepLabel
                        StepIconComponent={largeScreen ? () => <StepButton disabled={false} aria-label={stepIndex} className={currentStep === stepIndex || !completedStep ? 'default-cursor' : undefined} />
                          : () => <MobileStepIcon active={stepIndex === currentStep} isCompleted={completedStep} />}
                        className={`wizard-step ${completedStep ? 'step-completed' : ''}`}
                      >
                        {largeScreen && stepName}
                      </StepLabel>
                    </Step>
                  ))]}
                </Stepper>
              )}
            </FormWizard.Stepper>
            <Box
              sx={{
                px: { xs: 0, sm: 10 }, pl: { xs: currentStep > 0 ? 2 : 1 }, pr: { xs: 0 }, mb: { xs: 4.5, sm: 6 }, pt: { xs: 4, sm: 8 },
              }}
              pb={4}
            >
              {handleSteps(currentStep)}
            </Box>
          </div>
          <FormWizard.NavigationControls>
            <Grid container component={Box} px={0} sx={{ pl: { sm: currentStep !== STEPS.FILING_STATUS || (currentStep === STEPS.FILING_STATUS && mediumScreen) ? 8 : 0 }, ml: { xs: 0, sm: '-10px' } }} mt={-4} pb={4} flexDirection={currentStep !== STEPS.FILING_STATUS ? 'row' : 'column'} alignItems={mediumScreen ? 'flex-start' : 'center'}>
              <NavButton disabled={currentStep === STEPS.FILING_STATUS && !values.benefitEstimator.filingStatus} ml='auto' mr='auto' className='primary-btn continue-btn' name='Continue' handler='Next' variant='contained' btnClassName='nextStep' />
              <NavButton className='primary-btn' name='See your results' handler='Submit' variant='contained' btnClassName='nextStep' loading={isSubmitLoading} />
              {currentStep !== STEPS.FILING_STATUS && <NavButton className='ghost-btn' name='Back' handler='Back' variant='text' color='primary' />}
            </Grid>
          </FormWizard.NavigationControls>
        </>
      )}
    </FormWizard>
  );
};

export default Wizard;
